import DateFnsUtils from "@date-io/date-fns";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MaterialTable from "material-table";
import moment from "moment";
import React, { forwardRef, useEffect, useState, useContext } from "react";
import axios from "../../axiosinstance";
import PatchedPagination from "../layout/MTPagination";
import LayoutContext from "../../context/layout/LayoutContext";

const column = [
  {
    field: "shopify_order_id",
    title: "Shopify Order ID",
    editable: "never",
    cellStyle: {
      width: 150,
      maxWidth: 150,
      fontSize: 12,
      textAlign: "left",
    },
  },
  {
    field: "shopify_order_number",
    title: "Order Number",
    editable: "never",
    cellStyle: {
      width: 120,
      maxWidth: 120,
      fontSize: 12,
      textAlign: "left",
    },
  },
  {
    field: "paypal_trx_id",
    title: "Paypal Transaction ID",
    editable: "never",
    cellStyle: {
      width: 180,
      maxWidth: 180,
      fontSize: 12,
      textAlign: "left",
    },
  },
  {
    field: "customer_email",
    title: "Customer Email",
    editable: "never",
    cellStyle: {
      width: 160,
      maxWidth: 160,
      fontSize: 12,
      textAlign: "left",
    },
  },
  {
    field: "info_added",
    title: "Info Added ?",
    editable: "never",
    cellStyle: {
      width: 120,
      minWidth: 120,
      fontSize: 12,
      textAlign: "center",
    },
    render: (rowData) => (rowData.info_added ? <Check /> : <Clear />),
  },
  {
    field: "created_at",
    title: "Created At",
    editable: "never",
    cellStyle: {
      width: 100,
      minWidth: 100,
      fontSize: 12,
      textAlign: "left",
    },
    render: (rowData) => rowData.created_at.split("T")[0],
  },
];

const PaypalTracking = () => {
  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  const useStyles = makeStyles((theme) => ({
    root: {
      flexGrow: 1,
    },
    action: {
      backgroundColor: "white",
      marginTop: 5,
      marginBottom: 5,
    },
    message: {
      marginBottom: 15,
      color: "red",
      fontStyle: "italic",
      textAlign: "right"
    }
  }));

  const classes = useStyles();

  const { setAlert, setLoading } = useContext(LayoutContext);
  const [tableLoading, setTableLoading] = useState(true);
  const [dateFromSelected, setDateFromSelected] = useState(new Date());
  const [dateToSelected, setDateToSelected] = useState(new Date());

  const [rows, setRows] = useState([]);

  useEffect(() => {
    onDateSearch();
  }, []);

  const onDateSearch = async () => {
    setTableLoading(true);
    const date_from = moment(dateFromSelected).format("YYYY-MM-DD");
    const date_to = moment(dateToSelected).format("YYYY-MM-DD");

    try {
      const res = await axios.get(
        `/api/products/tracking/paypal?fromDate=${date_from}&toDate=${date_to}`
      );

      setRows(res.data);
    } catch (err) {
      console.log(err);
      setAlert(err?.response?.data?.message, "error");
    }
    setTableLoading(false);
  };

  const onAddTrackingInfo = async () => {
    setLoading(true);
    setTableLoading(true);
    const date_from = moment(dateFromSelected).format("YYYY-MM-DD");
    const date_to = moment(dateToSelected).format("YYYY-MM-DD");

    try {
      const res = await axios.post(`/api/products/tracking`, {
        fromDate: date_from,
        toDate: date_to,
      });

      if (res.status === 200) {
        setAlert(res.data.message, "success");
        await onDateSearch();
      }
    } catch (err) {
      console.log(err);
      setAlert(err?.response?.data?.message, "error");
    }
    setTableLoading(false);
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <Grid item xs={12}>
        <Typography variant="h4"> PayPal Tracking </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        container
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        className={classes.action}
      >
        <Grid
          item
          xs
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="yyyy-MM-dd"
              margin="dense"
              id="date_from"
              label="From"
              disabled={tableLoading}
              autoOk={true}
              value={dateFromSelected}
              onChange={(date) => setDateFromSelected(date)}
              style={{
                marginLeft: 5,
                marginRight: 10,
              }}
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="yyyy-MM-dd"
              margin="dense"
              id="date_to"
              label="To"
              disabled={tableLoading}
              autoOk={true}
              value={dateToSelected}
              onChange={(date) => setDateToSelected(date)}
              style={{
                marginRight: 10,
              }}
            />
          </MuiPickersUtilsProvider>
          <Button
            variant="contained"
            color="primary"
            onClick={onDateSearch}
            disabled={
              tableLoading ||
              moment(dateFromSelected).format("YYYY-MM-DD") >
                moment(dateToSelected).format("YYYY-MM-DD")
            }
          >
            Search
          </Button>
        </Grid>

        <Grid item justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={onAddTrackingInfo}
            disabled={
              tableLoading ||
              rows.length < 1 ||
              moment(dateFromSelected).format("YYYY-MM-DD") >
                moment(dateToSelected).format("YYYY-MM-DD")
            }
          >
            Update Tracking Info to PayPal
          </Button>
        </Grid>
      </Grid>

      <Typography className={classes.message}>
        ** PayPal API only supports maximum 20 objects to be added at once.
      </Typography>

      <Grid item xs={12}>
        <Divider variant="fullWidth" />
      </Grid>

      <Grid container spacing={1}>
        <Grid container spacing={1} justifyContent="flex-start">
          <Grid item xs={12}>
            <MaterialTable
              data={rows}
              columns={column}
              title=""
              icons={tableIcons}
              options={{
                sorting: true,
                toolbar: true,
                showTitle: true,
                pageSizeOptions: [10, 20, 30, 40],
                pageSize: 10,
                padding: "dense",
                headerStyle: {
                  fontWeight: "bold",
                  fontSize: 12,
                  textAlign: "left",
                  zIndex: 0,
                },
              }}
              components={{
                Pagination: PatchedPagination,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default PaypalTracking;
